// 遠端協助的 react-query hooks：範圍內的主機、協助請求、這個人對哪些家戶有遠端協助權限（kmgr 主機清單的 can_remote）。
import { useQueries, useQuery } from '@tanstack/react-query';
import { useScope } from '../../../lib/scope';
import { orgApi, type MyOrg, type OrgMember } from '../../../lib/planeApi';
import { kmgrApi } from '../api';
import { kmgrAbsent } from '../hooks';
import { kmgrScopeOf } from '../model';
import type { HostRow } from '../types';
import { remoteApi } from './api';
import type { AssistRequest } from './types';

export const ASSIST_REFRESH_MS = 15_000;

/** 範圍內的全部主機（GET /v1/me/kmgr/hosts 翻完，最多 10 頁）。 */
export function useScopeHosts() {
  const { planes, scope } = useScope();
  const api = planes[0]?.api ?? '';
  const ks = kmgrScopeOf(scope);
  return useQuery({
    queryKey: ['kmgr', 'remote', 'hosts', api, ks.scope],
    queryFn: async (): Promise<HostRow[]> => {
      const out: HostRow[] = [];
      let cursor: string | undefined;
      for (let i = 0; i < 10; i++) {
        const r = await kmgrApi.hosts(api, {
          scope: ks.scope,
          cursor,
          limit: 200,
        });
        out.push(...r.hosts);
        if (!r.next_cursor) break;
        cursor = r.next_cursor;
      }
      return out;
    },
    enabled: !!api,
    staleTime: 15_000,
    retry: (n: number, e: unknown) => !kmgrAbsent(e) && n < 1,
  });
}

/** 家戶的 api（家戶所在 plane）。 */
export function useApiOf(): (homeId: string) => string {
  const { homes, planes } = useScope();
  return (id: string) => homes.find((h) => h.home_id === id)?.endpoints?.api ?? planes[0]?.api ?? '';
}

/**
 * 範圍內我能遠端協助的家戶（kmgr 主機清單的 can_remote，M4-06b）。can_remote 沒帶（授權器讀不到）的家戶兩邊都不放。
 * checked = 每個家戶都有答案；全部 false = 協助請求分頁是權限不足的畫面（紀錄分頁另由 kmgr 的 access 決定）。
 */
export function remoteAccess(hosts: HostRow[] | undefined): { allowed: Set<string>; denied: Set<string>; checked: boolean } {
  const allowed = new Set<string>();
  const denied = new Set<string>();
  for (const h of hosts ?? []) {
    if (h.can_remote === true) allowed.add(h.home_id);
    else if (h.can_remote === false) denied.add(h.home_id);
  }
  for (const id of allowed) denied.delete(id);
  return { allowed, denied, checked: !!hosts?.length && (hosts ?? []).every((h) => h.can_remote !== undefined) };
}

/** 協助請求（每個有主機的組織一次，15 秒重抓）。 */
export function useAssistRequests(hosts: HostRow[] | undefined) {
  const apiOf = useApiOf();
  const orgs = new Map<string, string>();
  for (const h of hosts ?? []) if (h.org_id && !orgs.has(h.org_id)) orgs.set(h.org_id, apiOf(h.home_id));
  const entries = [...orgs.entries()];
  const qs = useQueries({
    queries: entries.map(([org, api]) => ({
      queryKey: ['kmgr', 'remote', 'assist', org],
      queryFn: () => remoteApi.assistRequests(api, org),
      refetchInterval: ASSIST_REFRESH_MS,
      retry: (n: number, e: unknown) => !kmgrAbsent(e) && n < 1,
    })),
  });
  const list: AssistRequest[] = qs.flatMap((q) => q.data ?? []).sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  return {
    list,
    loading: qs.some((q) => q.isLoading),
    failed: qs.filter((q) => q.isError && !q.data).length,
    updatedAt: Math.max(0, ...qs.map((q) => q.dataUpdatedAt || 0)),
    refetch: () => qs.forEach((q) => void q.refetch()),
  };
}

/** 我所在組織的成員（操作者名稱用；讀不到的組織略過）。 */
export function useMemberNames(orgs: MyOrg[]): (userId: string) => string | undefined {
  const qs = useQueries({
    queries: orgs.map((o) => ({
      queryKey: ['org-members', o.org_id],
      queryFn: () => orgApi.members(o),
      staleTime: 60_000,
      retry: false,
    })),
  });
  const all: OrgMember[] = qs.flatMap((q) => (q.data as OrgMember[] | undefined) ?? []);
  return (id: string) => {
    const m = all.find((x) => x.user_id === id);
    return m ? m.display_name || m.email : undefined;
  };
}
